/**
 * GameHeaderCard — the game "shop header" shown above the product grid (donate-shop
 * layout): game icon (rounded square) on the left, game name + a short neutral
 * subtitle on the right, plus a decorative "favourite" star.
 *
 * Pure presentation. The subtitle is caller-provided neutral copy (we never invent
 * backend fields such as an ETA on the game itself). The optional single "Информация"
 * tab mirrors the reference without claiming functionality that has no data behind it.
 */
import { Card } from "@melusine/shared-ui";

import type { Game } from "@entities/game";

import styles from "./GameHeaderCard.module.css";

export interface GameHeaderCardProps {
  /** Active game; ``undefined`` falls back to a neutral title. */
  readonly game?: Game | undefined;
  /** Title override when no game is resolved yet (e.g. category name). */
  readonly fallbackTitle?: string;
  /** Short neutral supporting line under the title. */
  readonly subtitle?: string;
  /** Render the single decorative "Информация" tab row. Defaults to ``true``. */
  readonly showInfoTab?: boolean;
}

const StarIcon = (): JSX.Element => (
  <svg
    width="18"
    height="18"
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="1.8"
    strokeLinecap="round"
    strokeLinejoin="round"
    aria-hidden="true"
  >
    <path d="M12 3.5l2.6 5.27 5.82.85-4.21 4.1.99 5.78L12 16.77 6.99 19.5l.99-5.78-4.21-4.1 5.82-.85z" />
  </svg>
);

/** Russian plural for "отзыв" (1 отзыв / 2 отзыва / 5 отзывов). */
function reviewsWord(n: number): string {
  const mod10 = n % 10;
  const mod100 = n % 100;
  if (mod10 === 1 && mod100 !== 11) {
    return "отзыв";
  }
  if (mod10 >= 2 && mod10 <= 4 && (mod100 < 10 || mod100 >= 20)) {
    return "отзыва";
  }
  return "отзывов";
}

export function GameHeaderCard({
  game,
  fallbackTitle = "Магазин",
  subtitle,
  showInfoTab = true,
}: GameHeaderCardProps): JSX.Element {
  const title = game?.name ?? fallbackTitle;

  return (
    <Card glow={false} className={styles.card}>
      <div className={styles.top}>
        <div className={styles.icon} aria-hidden="true">
          {game?.icon_url ? (
            <img src={game.icon_url} alt="" className={styles.iconImg} />
          ) : (
            <span className={styles.iconLabel}>
              {(game?.slug || game?.name || "✦").slice(0, 2).toUpperCase()}
            </span>
          )}
        </div>
        <div className={styles.text}>
          <h2 className={styles.name}>{title}</h2>
          {game && game.reviews_count > 0 ? (
            <p className={styles.rating}>
              <span className={styles.ratingStar} aria-hidden="true">
                ★
              </span>
              {game.rating_avg.toFixed(1)} · {game.reviews_count}{" "}
              {reviewsWord(game.reviews_count)}
            </p>
          ) : subtitle ? (
            <p className={styles.subtitle}>{subtitle}</p>
          ) : null}
        </div>
        <span className={styles.star} aria-hidden="true">
          <StarIcon />
        </span>
      </div>
      {showInfoTab ? (
        <div className={styles.tabs} role="presentation">
          <span className={styles.tab} data-active="true">
            Информация
          </span>
        </div>
      ) : null}
    </Card>
  );
}
